/**
 * In-place zoom for the PDP gallery.
 *
 * Clicking a gallery image used to open <modal-opener>'s pop-up, which the
 * viewer then had to scroll. Instead the image now scales inside its own frame,
 * with the origin following the pointer, and a second click (or leaving the
 * image) returns it. The "+" in the corner is a marker only — it never handles
 * the click, the full-size .product__media-toggle button does.
 *
 * The click is intercepted on <modal-opener> during the CAPTURE phase so it
 * never reaches the toggle's own listener, which is what opens the modal.
 */
(function () {
  // The product section re-renders on variant change and re-runs its scripts.
  // Without this guard a second copy of the in-place gallery zoom binds its own document
  // listeners, and the two cancel each other out on alternate interactions.
  if (window.__weldunImageZoomLoaded) return;
  window.__weldunImageZoomLoaded = true;

  var ZOOMED = 'is-zoomed';
  var ZOOMED_SELECTOR = 'product__modal-opener--image.is-zoomed';
  var DESKTOP = '(min-width: 990px)';

  function setOrigin(media, event) {
    var rect = media.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var x = ((event.clientX - rect.left) / rect.width) * 100;
    var y = ((event.clientY - rect.top) / rect.height) * 100;
    media.style.setProperty('--zoom-x', Math.max(0, Math.min(100, x)) + '%');
    media.style.setProperty('--zoom-y', Math.max(0, Math.min(100, y)) + '%');
  }

  function bind(opener) {
    if (opener.dataset.inPlaceZoom === 'true') return;
    opener.dataset.inPlaceZoom = 'true';

    var media = opener.querySelector('.product__media');
    if (!media) return;

    opener.addEventListener('mousemove', function (event) {
      if (opener.classList.contains(ZOOMED)) setOrigin(media, event);
    });

    opener.addEventListener('mouseleave', function () {
      opener.classList.remove(ZOOMED);
    });
  }

  // One capture listener on the document, so it runs before <modal-opener>'s
  // own click handler (which is bound to the opener element itself and would
  // otherwise still open the pop-up).
  document.addEventListener(
    'click',
    function (event) {
      if (!window.matchMedia(DESKTOP).matches) return;
      // A keyboard activation (Enter/Space on the toggle) reports detail 0 and
      // carries no pointer coordinates, so the pointer-anchored zoom below has
      // nothing to anchor to. Let those through to <modal-opener>, which opens
      // the lightbox — the accessible equivalent, and what the button's
      // aria-haspopup="dialog" already promises.
      if (event.detail === 0) return;
      var target = event.target;
      if (!target || !target.closest) return;
      var opener = target.closest('.product__modal-opener--image');
      if (!opener) return;
      var media = opener.querySelector('.product__media');
      if (!media) return;
      event.preventDefault();
      event.stopPropagation();
      var wasZoomed = opener.classList.contains(ZOOMED);
      // A click means "zoom this one in" or "zoom out" — never a blind toggle of
      // whatever state was left behind by a previous colour, which is what made
      // every second colour appear not to zoom.
      clearZoom();
      if (!wasZoomed) {
        setOrigin(media, event);
        opener.classList.add(ZOOMED);
      }
    },
    true
  );

  // The theme keeps the same opener elements across a colour change, so an
  // .is-zoomed left over from the previous colour would make the next click
  // toggle it off instead of on. Clear it whenever the variant changes.
  function clearZoom() {
    document.querySelectorAll('.' + ZOOMED_SELECTOR).forEach(function (opener) {
      opener.classList.remove(ZOOMED);
    });
  }

  // Only a genuine variant switch should reset zoom. The theme also re-dispatches
  // change on the picker when the gallery syncs media — including in response to
  // the zoom click itself — which was clearing the zoom the instant it applied.
  var lastVariant = null;

  // Only the FIRST option (colour) matters: that is what re-groups the gallery.
  // Clicking a media item makes the theme re-sync the variant and can flip the
  // selected size, which is not a reason to drop the zoom.
  function currentVariant() {
    var first = document.querySelector('.product-form__input');
    if (!first) return '';
    var checked = first.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    var select = first.querySelector('select');
    return select ? select.value : '';
  }

  lastVariant = currentVariant();

  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('.product-form__input')) return;
      var next = currentVariant();
      if (next === lastVariant) return;
      lastVariant = next;
      clearZoom();
    },
    true
  );

  function scan(root) {
    (root || document).querySelectorAll('.product__modal-opener--image').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // Variant switches re-render the gallery, so pick up newly inserted images.
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        // No clearZoom here: a freshly inserted opener never carries the class,
        // and the theme inserts nodes in response to the zoom click itself — so
        // clearing here wiped the zoom the moment it was applied.
        if (node.matches && node.matches('.product__modal-opener--image')) bind(node);
        else scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
